import { InfoBanner } from "@screencloud/screencloud-ui-components";
import { type InjectedIntlProps, injectIntl } from "react-intl";
import styled from "styled-components";
import { INVOICE_MIN_SCREENS } from "../constants";

/**
 * InfoBanner's `.core` is `height: 100%` with `justify-content: flex-start`, so
 * a single-line description sits at the TOP of the banner rather than centered.
 * And its `.icon` is a block whose baseline line-box leaves the glyph sitting
 * high. Center everything vertically: center the row, center the content inside
 * the full-height core, and flex-center the icon glyph (dropping its top nudge).
 * `&&` doubles specificity so these beat InfoBanner's own rules regardless of
 * stylesheet order.
 */
const CenteredInfoBanner = styled(InfoBanner)`
  && {
    align-items: center;
  }

  && > div.icon {
    display: flex;
    align-items: center;
  }

  && > div.icon i {
    margin-top: 0;
  }

  && > div.core {
    justify-content: center;
  }
`;

interface InvoiceNoticeProps {
  /**
   * True when Invoice is selected for an order below {@link INVOICE_MIN_SCREENS}.
   * Turns the banner into an error state that explains why Continue is blocked.
   */
  belowMinimum?: boolean;
}

/**
 * Banner shown when the Invoice payment method is selected. Mirrors the
 * billinglatest invoice notice — no fields to fill; the actual
 * `createPaymentSourceInvoice` call happens at Checkout.
 *
 * When the order is below the screen minimum it switches to an error state that
 * explains why Continue is blocked, so the rule is enforced, not just stated.
 * The screen minimum is interpolated from {@link INVOICE_MIN_SCREENS} in both
 * copies so it can't drift from the validation.
 */
function InvoiceNoticeBase({
  intl,
  belowMinimum = false,
}: InvoiceNoticeProps & InjectedIntlProps) {
  const description = belowMinimum
    ? intl.formatMessage(
        {
          id: "pixi_wizard.payment_shipping.invoice_min_screens_error",
          defaultMessage:
            "Unfortunately, invoice billing is only available for orders of {min} screens or more. You can add more screens or choose a different payment method.",
        },
        { min: INVOICE_MIN_SCREENS },
      )
    : intl.formatMessage(
        {
          id: "pixi_wizard.payment_shipping.invoice_notice",
          defaultMessage:
            "Invoice billing is available as an annual option only, with a minimum license of {min} screens.",
        },
        { min: INVOICE_MIN_SCREENS },
      );

  return (
    <CenteredInfoBanner
      color={belowMinimum ? "error" : "warning"}
      icon="info"
      data-testid="invoice-notice"
      description={description}
    />
  );
}

export const InvoiceNotice = injectIntl(InvoiceNoticeBase);
